// 实时画面（任务还没开始）：任务一建好就在它的 Chrome 里打开入口待命，这里直接看画面。
// 状态：绿 = 已拉起待命；黄 = Chrome 被别的任务占着在排队；红框 = 拉不起来（写原因，可「重新拉起」）
import { useEffect, useRef, useState } from "react";
import type { TaskDTO, TaskStandby } from "@csp/shared";
import { api, errorText } from "../lib/api";
import { VncViewer } from "./VncViewer";

export function standbyText(sb: TaskStandby | null | undefined): { tone: "ok" | "warn" | "bad" | "idle"; text: string } {
  if (!sb) return { tone: "idle", text: "正在拉起浏览器…" };
  if (sb.state === "ready") return { tone: "ok", text: `等待开始 · ${sb.slotLabel ?? sb.slot ?? ""}` };
  if (sb.state === "queued") return { tone: "warn", text: `排队等「${sb.slotLabel ?? sb.slot}」的 Chrome${sb.waitingFor ? `（#${sb.waitingFor} 在用）` : ""}` };
  return { tone: "bad", text: "浏览器拉不起来" };
}

/** 画面区标题右侧的状态胶囊 */
export function StandbyBadge({ standby }: { standby: TaskStandby | null | undefined }) {
  const { tone, text } = standbyText(standby);
  return <span className={`sb-badge sb-${tone}`} role="status" data-testid="standby-status"><i aria-hidden />{text}</span>;
}

export function StandbyScreen({ task, canAct, live, onChanged }: { task: TaskDTO; canAct: boolean; live: boolean; onChanged: () => void }) {
  const sb = task.standby ?? null;
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const asked = useRef<string | null>(null);
  const pull = async (force: boolean) => {
    setBusy(true); setError(null);
    try { await api.standby(task.id, force); onChanged(); }
    catch (e) { setError(errorText(e)); }
    finally { setBusy(false); }
  };
  // 打开任务：还没待命（或超时让出了）就拉起；每个任务只自动拉一次，失败了等人点「重新拉起」
  useEffect(() => {
    if (!canAct || asked.current === task.id) return;
    if (sb?.state === "ready" || sb?.state === "failed") return;
    asked.current = task.id;
    void pull(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [task.id, canAct, sb?.state]);

  if (sb?.state === "ready") {
    return live && canAct ? <VncViewer key={sb.tabId ?? ""} taskId={task.id} mode="view" height={420} /> : <div className="workspace-screen-empty">浏览器已在「{sb.slotLabel}」打开入口，等待开始</div>;
  }
  if (sb?.state === "failed") {
    return (
      <div className="sb-fail" data-testid="standby-failed">
        <b>⚠ 浏览器拉不起来</b>
        <p>{sb.error}</p>
        {sb.slotLabel && <p className="small">Chrome：{sb.slotLabel}{sb.url ? ` · 入口 ${sb.url}` : ""}</p>}
        {error && <p className="small">{error}</p>}
        {canAct && <button className="btn btn-danger" disabled={busy} onClick={() => void pull(true)}>{busy ? "正在拉起…" : "重新拉起"}</button>}
      </div>
    );
  }
  if (sb?.state === "queued") {
    return (
      <div className="sb-wait" data-testid="standby-queued">
        <b>{standbyText(sb).text}</b>
        <p className="small">一个 Chrome 同一时间只给一个任务用。前面的任务开始跑完、取消或待命超时后，这里自动拉起。</p>
        {canAct && <button className="btn btn-sm" disabled={busy} onClick={() => void pull(false)}>{busy ? "检查中…" : "再看一下空了没有"}</button>}
        {error && <p className="small err">{error}</p>}
      </div>
    );
  }
  return <div className="workspace-screen-empty">{error ? <span className="err">{error}</span> : "正在拉起浏览器…"}</div>;
}
